import { apiUrl } from '../lib/config';
import { errorText } from '../lib/labels';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Korean text suitable for showing to a person. */
  get text(): string {
    return errorText(this.code, this.message);
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Best-effort Korean message for anything thrown by the API layer. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.text;
  if (e instanceof TypeError) return errorText('network');
  if (e instanceof Error && e.message) return e.message;
  return errorText('internal');
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * JSON call to the server (same origin in development, its own hostname in production).
 * Throws `ApiError` for non-2xx replies using the server's `{error}` shape.
 */
export async function api<T = unknown>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? (res.status === 401 ? 'unauthorized' : 'http_error'),
      err?.message ?? `요청 실패 (${res.status})`,
    );
  }
  return json as T;
}
